import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import BookingChat from '@/components/BookingChat/BookingChat';
import { tableBookingWebSocketService } from '@/services/tableBookingWebSocket';
import { apiService } from '@/services/api';
import styles from './MyBookings.module.scss';

/** Real booking shape returned by GET /customers/bookings. */
interface Booking {
  id: string;
  tableId: string;
  branchId: string;
  status: string; // requested | confirmed | active | completed | cancelled | rejected | no_show
  bookingDate: string; // YYYY-MM-DD
  startTime: string; // HH:MM:SS
  endTime: string;
  numberOfCustomers: number;
  specialRequests?: string | null;
  checkedIn?: boolean;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  paymentAmount?: number;
  depositAmount?: number;
  depositStatus?: string;
  rescheduledAt?: string | null;
  preorderNotes?: string | null;
  customerRating?: number | null;
  customerFeedback?: string | null;
  rescheduleRequestStatus?: string | null;
  rescheduleRequestedDate?: string | null;
  rescheduleRequestedStartTime?: string | null;
  checkInCode?: string | null;
  table?: { tableNumber?: number | string; capacity?: number; location?: string } | null;
  branch?: { name?: string; address?: string; city?: string; state?: string } | null;
}

interface MyBookingsState {
  bookings: Booking[];
  activeTab: 'upcoming' | 'past' | 'all';
  isLoading: boolean;
  error: string | null;
  selectedBooking: Booking | null;
  showDetailModal: boolean;
  preorderDraft: string;
  savingPreorder: boolean;
  ratingBooking: Booking | null;
  ratingValue: number;
  ratingText: string;
  savingRating: boolean;
  reschedBooking: Booking | null;
  reschedDate: string;
  reschedTime: string;
  reschedReason: string;
  savingResched: boolean;
  openChatBookingId: string | null;
  galleryConsent: any[]; // gallery posts that feature this customer, awaiting their yes/no
  socketNotification: {
    show: boolean;
    type: 'success' | 'error' | 'info';
    title: string;
    message: string;
  } | null;
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

// Organizer-driven lifecycle events that should refresh the customer's list + toast.
const LIFECYCLE_EVENTS: Array<{ event: string; type: 'success' | 'error' | 'info'; title: string; message: (d: any) => string }> = [
  { event: 'table_booking:approved', type: 'success', title: 'Booking confirmed', message: (d) => `Your booking${d?.tableNumber ? ` for Table ${d.tableNumber}` : ''} was confirmed.` },
  { event: 'table_booking:rejected', type: 'error', title: 'Booking rejected', message: (d) => d?.rejectComments || 'Your booking request was not approved.' },
  { event: 'table_booking:cancelled', type: 'error', title: 'Booking cancelled', message: (d) => d?.cancellationReason || 'Your booking was cancelled.' },
  { event: 'table_booking:checked_in', type: 'success', title: 'Checked in', message: () => 'You have been checked in. Enjoy your visit!' },
  { event: 'table_booking:completed', type: 'info', title: 'Visit completed', message: () => 'Your booking is complete. Thanks for visiting!' },
  { event: 'table_booking:no_show', type: 'error', title: 'Marked as no-show', message: () => 'Your booking was marked as a no-show.' },
  { event: 'table_booking:rescheduled', type: 'info', title: 'Booking rescheduled', message: (d) => `Your booking was moved${d?.bookingDate ? ` to ${String(d.bookingDate).slice(0, 10)}` : ''}${d?.startTime ? ` at ${String(d.startTime).slice(0, 5)}` : ''}.` },
  { event: 'table_booking:deposit', type: 'info', title: 'Deposit updated', message: (d) => `Deposit ${d?.depositStatus || 'recorded'}${d?.depositAmount != null ? `: ${d.depositAmount}` : ''}.` },
  { event: 'table_booking:preorder', type: 'info', title: 'Pre-order updated', message: () => 'Your pre-order was updated.' },
  { event: 'booking_waitlist:notified', type: 'success', title: 'A table is free!', message: () => 'A table you waitlisted for is now available — book it before it goes.' },
  { event: 'table_booking:reschedule_declined', type: 'error', title: 'Reschedule declined', message: (d) => d?.reason || 'Your reschedule request was declined — your original time still stands.' },
  { event: 'table_booking:expired', type: 'error', title: 'Request expired', message: () => 'Your booking request expired before it was approved. Please try again.' },
];

class MyBookings extends Component<any, MyBookingsState> {
  private notifTimer: any = null;
  private pendingRateBookingId: string | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      bookings: [],
      activeTab: 'all',
      isLoading: true,
      error: null,
      selectedBooking: null,
      showDetailModal: false,
      preorderDraft: '',
      savingPreorder: false,
      ratingBooking: null,
      ratingValue: 0,
      ratingText: '',
      savingRating: false,
      reschedBooking: null,
      reschedDate: '',
      reschedTime: '',
      reschedReason: '',
      savingResched: false,
      openChatBookingId: null,
      galleryConsent: [],
      socketNotification: null,
    };
  }

  componentDidMount() {
    const rateParams = new URLSearchParams(window.location.search);
    if (rateParams.get('rate') === '1' && rateParams.get('bookingId')) {
      this.pendingRateBookingId = rateParams.get('bookingId');
    }
    // Feedback after a "claim my session" (guest → registered) hand-off, which reloads to this screen.
    try {
      if (sessionStorage.getItem('qrClaimSuccess')) {
        sessionStorage.removeItem('qrClaimSuccess');
        this.showNotification('success', 'Visit saved', 'Your table, order and bill are now on your account.');
      } else {
        const claimErr = sessionStorage.getItem('qrClaimError');
        if (claimErr) {
          sessionStorage.removeItem('qrClaimError');
          this.showNotification('error', 'Could not save your visit', claimErr);
        }
      }
    } catch { /* private mode */ }
    this.loadBookings();
    this.setupSocketListeners();
    // Gallery consent: photos a venue tagged this customer in, awaiting their approval.
    apiService.getGalleryAwaitingConsent()
      .then((r: any) => this.setState({ galleryConsent: (r?.posts) || (r?.data?.posts) || [] }))
      .catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const openChat = params.get('openChat');
    const organizerId = params.get('organizerId');
    const branchId = params.get('branchId');
    if (openChat && openChat.trim()) {
      if (organizerId && branchId && this.props.navigate) {
        this.props.navigate(
          `/customer/organizers/${encodeURIComponent(organizerId)}/branches/${encodeURIComponent(branchId)}/tables?openChat=${encodeURIComponent(openChat.trim())}`,
          { replace: true }
        );
        return;
      }
      this.setState({ openChatBookingId: openChat.trim() });
      try {
        apiService.setLastChat({ type: 'booking_chat', conversationId: openChat.trim() });
      } catch (_) {}
    }
  }

  componentWillUnmount() {
    this.removeSocketListeners();
    if (this.notifTimer) clearTimeout(this.notifTimer);
  }

  setupSocketListeners = () => {
    tableBookingWebSocketService.connect();
    LIFECYCLE_EVENTS.forEach(({ event }) => tableBookingWebSocketService.on(event, this.handleLifecycleEvent(event)));
    tableBookingWebSocketService.on('error', this.handleSocketError);
  };

  removeSocketListeners = () => {
    LIFECYCLE_EVENTS.forEach(({ event }) => tableBookingWebSocketService.off(event, this.handleLifecycleEvent(event)));
    tableBookingWebSocketService.off('error', this.handleSocketError);
  };

  // Cache one bound handler per event so on()/off() reference the same function.
  private handlerCache: Record<string, (data: any) => void> = {};
  handleLifecycleEvent = (event: string) => {
    if (!this.handlerCache[event]) {
      const cfg = LIFECYCLE_EVENTS.find((e) => e.event === event)!;
      this.handlerCache[event] = (data: any) => {
        this.showNotification(cfg.type, cfg.title, cfg.message(data || {}));
        // Re-fetch so the list reflects the authoritative server state.
        this.loadBookings();
      };
    }
    return this.handlerCache[event];
  };

  handleSocketError = (error: any) => {
    console.error('[MyBookings] Socket error:', error);
  };

  showNotification = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    this.setState({ socketNotification: { show: true, type, title, message } });
    if (this.notifTimer) clearTimeout(this.notifTimer);
    this.notifTimer = setTimeout(() => this.setState({ socketNotification: null }), 6000);
  };

  handleCloseNotification = () => this.setState({ socketNotification: null });

  decideGalleryConsent = async (postId: string, approve: boolean) => {
    try {
      await apiService.setGalleryConsent(postId, approve);
      this.setState((s) => ({ galleryConsent: s.galleryConsent.filter((p: any) => p.id !== postId) }));
      this.showNotification('success', approve ? 'Photo approved' : 'Photo hidden', approve ? 'Members can see it now.' : 'It won’t be shared.');
    } catch {
      this.showNotification('error', 'Could not update', 'Please try again.');
    }
  };

  loadBookings = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const res = await apiService.getMyBookings();
      if (res.success && res.data?.bookings) {
        const bookings = res.data.bookings as Booking[];
        this.setState({ bookings, isLoading: false });
        // Auto-open the rating modal when arriving from a "rate your visit" push.
        if (this.pendingRateBookingId) {
          const target = bookings.find((b) => b.id === this.pendingRateBookingId);
          this.pendingRateBookingId = null;
          if (target && target.status === 'completed') this.openRating(target);
        }
      } else {
        this.setState({ bookings: [], isLoading: false, error: res.message || 'Failed to load bookings' });
      }
    } catch (err) {
      this.setState({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load bookings' });
    }
  };

  handleTabChange = (tab: 'upcoming' | 'past' | 'all') => this.setState({ activeTab: tab });

  handleBookingClick = (booking: Booking) => {
    this.setState({ selectedBooking: booking, showDetailModal: true, preorderDraft: booking.preorderNotes || '' });
  };

  handleCloseModal = () => this.setState({ showDetailModal: false, selectedBooking: null });

  handleCloseBookingChat = () => {
    this.setState({ openChatBookingId: null });
    const url = new URL(window.location.href);
    url.searchParams.delete('openChat');
    window.history.replaceState({}, '', url.pathname + url.search);
  };

  handleOpenChat = (bookingId: string) => this.setState({ openChatBookingId: bookingId });

  handleCancelBooking = async (bookingId: string) => {
    const reason = window.prompt('Reason for cancelling this booking?', 'Change of plans');
    if (reason === null) return;
    try {
      const res = await apiService.cancelBooking(bookingId, reason || 'Cancelled by customer');
      if (res.success) {
        this.showNotification('info', 'Booking cancelled', 'Your booking has been cancelled.');
        this.loadBookings();
      } else {
        this.showNotification('error', 'Could not cancel', res.message || 'Please try again.');
      }
    } catch (err) {
      this.showNotification('error', 'Could not cancel', err instanceof Error ? err.message : 'Please try again.');
    }
  };

  handleSavePreorder = async () => {
    const booking = this.state.selectedBooking;
    if (!booking) return;
    this.setState({ savingPreorder: true });
    try {
      const notes = this.state.preorderDraft.trim();
      const res = await apiService.setBookingPreorder(booking.id, notes ? notes : null);
      if (res.success) {
        this.showNotification('success', 'Pre-order saved', 'Your pre-order was sent to the venue.');
        this.setState({ savingPreorder: false });
        this.handleCloseModal();
        this.loadBookings();
      } else {
        this.setState({ savingPreorder: false });
        this.showNotification('error', 'Could not save pre-order', res.message || 'Please try again.');
      }
    } catch (err) {
      this.setState({ savingPreorder: false });
      this.showNotification('error', 'Could not save pre-order', err instanceof Error ? err.message : 'Please try again.');
    }
  };

  openRating = (booking: Booking) => {
    this.setState({ ratingBooking: booking, ratingValue: booking.customerRating || 0, ratingText: booking.customerFeedback || '' });
  };

  closeRating = () => this.setState({ ratingBooking: null });

  submitRating = async () => {
    const booking = this.state.ratingBooking;
    if (!booking || this.state.ratingValue < 1) {
      this.showNotification('error', 'Pick a rating', 'Please tap a star to rate your visit.');
      return;
    }
    this.setState({ savingRating: true });
    try {
      const res = await apiService.submitFeedback(booking.id, this.state.ratingValue, this.state.ratingText.trim() || undefined);
      if (res.success) {
        this.showNotification('success', 'Thanks for your feedback!', 'Your rating has been submitted.');
        this.setState({ savingRating: false, ratingBooking: null });
        this.loadBookings();
      } else {
        this.setState({ savingRating: false });
        this.showNotification('error', 'Could not submit', res.message || 'Please try again.');
      }
    } catch (err) {
      this.setState({ savingRating: false });
      this.showNotification('error', 'Could not submit', err instanceof Error ? err.message : 'Please try again.');
    }
  };

  canRate = (b: Booking) => b.status === 'completed';
  canStartOrder = (b: Booking) => b.status === 'active';

  handleStartOrder = async (bookingId: string) => {
    try {
      const res = await apiService.startOrder(bookingId);
      if (res.success) {
        this.showNotification('success', 'Order started', 'Your order has been started. Enjoy!');
        this.loadBookings();
      } else {
        this.showNotification('error', 'Could not start order', res.message || 'Please try again.');
      }
    } catch (err) {
      this.showNotification('error', 'Could not start order', err instanceof Error ? err.message : 'Please try again.');
    }
  };

  canRequestReschedule = (b: Booking) =>
    ['requested', 'confirmed'].includes(b.status) && b.rescheduleRequestStatus !== 'pending';

  openReschedule = (booking: Booking) => {
    this.setState({
      reschedBooking: booking,
      reschedDate: booking.bookingDate || new Date().toISOString().slice(0, 10),
      reschedTime: booking.startTime ? booking.startTime.slice(0, 5) : '',
      reschedReason: '',
    });
  };

  closeReschedule = () => this.setState({ reschedBooking: null });

  submitReschedule = async () => {
    const booking = this.state.reschedBooking;
    if (!booking) return;
    if (!this.state.reschedDate || !this.state.reschedTime) {
      this.showNotification('error', 'Pick a new time', 'Choose the date and start time you would like.');
      return;
    }
    this.setState({ savingResched: true });
    try {
      const res = await apiService.requestReschedule(booking.id, {
        bookingDate: this.state.reschedDate,
        startTime: this.state.reschedTime,
        reason: this.state.reschedReason.trim() || undefined,
      });
      if (res.success) {
        this.showNotification('success', 'Reschedule requested', 'The venue will review your request.');
        this.setState({ savingResched: false, reschedBooking: null });
        this.loadBookings();
      } else {
        this.setState({ savingResched: false });
        this.showNotification('error', 'Could not request', res.message || 'Please try again.');
      }
    } catch (err) {
      this.setState({ savingResched: false });
      this.showNotification('error', 'Could not request', err instanceof Error ? err.message : 'Please try again.');
    }
  };

  isUpcoming = (b: Booking): boolean => {
    const today = new Date().toISOString().slice(0, 10);
    const activeStatus = ['requested', 'pending', 'approved', 'confirmed', 'active', 'order_started'].includes(b.status);
    return activeStatus && (b.bookingDate >= today);
  };

  getFilteredBookings = (): Booking[] => {
    const { bookings, activeTab } = this.state;
    if (activeTab === 'upcoming') return bookings.filter(this.isUpcoming);
    if (activeTab === 'past') return bookings.filter((b) => !this.isUpcoming(b));
    return bookings;
  };

  getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { label: string; color: string; icon: string } } = {
      requested: { label: 'Requested', color: '#f59e0b', icon: '🟡' },
      pending: { label: 'Requested', color: '#f59e0b', icon: '🟡' },
      approved: { label: 'Approved', color: '#10b981', icon: '🟢' },
      confirmed: { label: 'Confirmed', color: '#10b981', icon: '🟢' },
      active: { label: 'Checked-in', color: '#3b82f6', icon: '🔵' },
      order_started: { label: 'Order Started', color: '#6366f1', icon: '🍽️' },
      completed: { label: 'Completed', color: '#3b82f6', icon: '✅' },
      cancelled: { label: 'Cancelled', color: '#6b7280', icon: '⚫' },
      rejected: { label: 'Rejected', color: '#ef4444', icon: '🔴' },
      no_show: { label: 'No-show', color: '#ef4444', icon: '🚫' },
      expired: { label: 'Expired', color: '#6b7280', icon: '⌛' },
    };
    const config = statusConfig[status] || statusConfig.requested;
    return (
      <span className={styles.statusBadge} style={{ backgroundColor: `${config.color}20`, color: config.color }}>
        {config.icon} {config.label}
      </span>
    );
  };

  formatDateTime = (dateStr: string, timeStr?: string) => {
    const iso = timeStr ? `${dateStr}T${timeStr}` : dateStr;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return `${dateStr}${timeStr ? ' ' + timeStr.slice(0, 5) : ''}`;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  canPreorder = (b: Booking) => ['requested', 'confirmed', 'active'].includes(b.status);
  canCancel = (b: Booking) => ['requested', 'confirmed'].includes(b.status);

  render() {
    const { activeTab, isLoading, error, selectedBooking, showDetailModal, socketNotification } = this.state;
    const filteredBookings = this.getFilteredBookings();

    return (
      <>
        <DashboardHeader />
        {socketNotification && socketNotification.show && (
          <div className={`${styles.socketNotification} ${styles[socketNotification.type]}`}>
            <div className={styles.notificationContent}>
              <div className={styles.notificationIcon}>
                {socketNotification.type === 'success' && '✅'}
                {socketNotification.type === 'error' && '❌'}
                {socketNotification.type === 'info' && 'ℹ️'}
              </div>
              <div className={styles.notificationText}>
                <div className={styles.notificationTitle}>{socketNotification.title}</div>
                <div className={styles.notificationMessage}>{socketNotification.message}</div>
              </div>
              <button className={styles.notificationClose} onClick={this.handleCloseNotification} aria-label="Close notification">×</button>
            </div>
          </div>
        )}
        {this.state.galleryConsent.length > 0 && (
          <div style={{ margin: '12px 16px 0', padding: 14, borderRadius: 12, border: '1px solid #f0d8cc', background: '#fff7f4' }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: '#7a2f12' }}>📸 You appear in {this.state.galleryConsent.length} venue {this.state.galleryConsent.length === 1 ? 'photo' : 'photos'}</div>
            {this.state.galleryConsent.map((p: any) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                {p.mediaUrl && p.mediaType !== 'video'
                  ? <img src={p.mediaUrl} alt="" style={{ width: 46, height: 46, borderRadius: 8, objectFit: 'cover' }} />
                  : <div style={{ width: 46, height: 46, borderRadius: 8, background: '#ffe9e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📷</div>}
                <span style={{ flex: 1, fontSize: '0.85rem', color: '#374151' }}>{p.caption || 'The venue captured a moment from your visit — allow members to see it?'}</span>
                <button type="button" onClick={() => this.decideGalleryConsent(p.id, false)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer' }}>Hide</button>
                <button type="button" onClick={() => this.decideGalleryConsent(p.id, true)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>Allow</button>
              </div>
            ))}
          </div>
        )}
        <div className={styles.myBookingsContainer}>
          <div className={styles.myBookingsContent}>
            <div className={styles.headerSection}>
              <h1 className={styles.pageTitle}>My Bookings</h1>
              <p className={styles.pageSubtitle}>Manage your table bookings and reservations</p>
              <button
                type="button"
                onClick={() => this.props.navigate('/customer/waitlist')}
                style={{
                  marginTop: '0.5rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.4rem 0.9rem',
                  borderRadius: 999,
                  border: '1px solid var(--accent-orange, #FF6B35)',
                  background: 'transparent',
                  color: 'var(--accent-orange, #FF6B35)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                🔔 My Waitlist
              </button>
            </div>

            <div className={styles.tabsContainer}>
              <button className={`${styles.tab} ${activeTab === 'all' ? styles.tabActive : ''}`} onClick={() => this.handleTabChange('all')}>All Bookings</button>
              <button className={`${styles.tab} ${activeTab === 'upcoming' ? styles.tabActive : ''}`} onClick={() => this.handleTabChange('upcoming')}>Upcoming</button>
              <button className={`${styles.tab} ${activeTab === 'past' ? styles.tabActive : ''}`} onClick={() => this.handleTabChange('past')}>Past</button>
            </div>

            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading bookings...</p>
              </div>
            ) : error ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>⚠️</div>
                <h3>Couldn't load bookings</h3>
                <p>{error}</p>
                <button onClick={this.loadBookings} className={styles.emptyStateButton}>Retry</button>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📅</div>
                <h3>No bookings found</h3>
                <p>
                  {activeTab === 'upcoming' ? "You don't have any upcoming bookings"
                    : activeTab === 'past' ? "You don't have any past bookings"
                    : "You haven't made any bookings yet"}
                </p>
                <button onClick={() => this.props.navigate('/customer/organizers')} className={styles.emptyStateButton}>Browse Organizers</button>
              </div>
            ) : (
              <div className={styles.bookingsList}>
                {filteredBookings.map((booking) => (
                  <div key={booking.id} className={styles.bookingCard}>
                    <div className={styles.bookingHeader}>
                      <div className={styles.bookingInfo}>
                        <h3 className={styles.bookingTitle}>{booking.branch?.name || 'Venue'}</h3>
                        <p className={styles.bookingTable}>Table {booking.table?.tableNumber ?? ''}</p>
                      </div>
                      {this.getStatusBadge(booking.status)}
                    </div>

                    <div className={styles.bookingDetails}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>📅 Date & Time:</span>
                        <span className={styles.detailValue}>{this.formatDateTime(booking.bookingDate, booking.startTime)}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>👥 Guests:</span>
                        <span className={styles.detailValue}>{booking.numberOfCustomers}</span>
                      </div>
                      {booking.branch?.address && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>📍 Location:</span>
                          <span className={styles.detailValue}>{booking.branch.address}</span>
                        </div>
                      )}
                      {booking.depositStatus && booking.depositStatus !== 'none' && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>💳 Deposit:</span>
                          <span className={styles.detailValue}>{booking.depositAmount} ({booking.depositStatus})</span>
                        </div>
                      )}
                      {booking.preorderNotes && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>🍽️ Pre-order:</span>
                          <span className={styles.detailValue}>{booking.preorderNotes}</span>
                        </div>
                      )}
                      {booking.checkInCode && (booking.status === 'confirmed' || booking.status === 'active') && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>🔑 Check-in code:</span>
                          <span className={styles.detailValue} style={{ fontWeight: 800, letterSpacing: '0.15em', fontSize: '1.05rem' }}>
                            {booking.checkInCode}
                          </span>
                        </div>
                      )}
                      {booking.rescheduledAt && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>🔄 Rescheduled</span>
                          <span className={styles.detailValue}>{this.formatDateTime(booking.rescheduledAt)}</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.bookingActions}>
                      <button className={styles.viewDetailsButton} onClick={() => this.handleBookingClick(booking)}>View Details</button>
                      <button className={styles.viewDetailsButton} onClick={() => this.handleOpenChat(booking.id)}>Chat</button>
                      {this.canStartOrder(booking) && (
                        <button className={styles.arriveButton} onClick={() => this.handleStartOrder(booking.id)}>Start Order</button>
                      )}
                      {this.canPreorder(booking) && (
                        <button className={styles.arriveButton} onClick={() => this.handleBookingClick(booking)}>Pre-order</button>
                      )}
                      {booking.rescheduleRequestStatus === 'pending' ? (
                        <span className={styles.statusBadge} style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}>
                          ⏳ Reschedule pending
                        </span>
                      ) : this.canRequestReschedule(booking) && (
                        <button className={styles.viewDetailsButton} onClick={() => this.openReschedule(booking)}>Request reschedule</button>
                      )}
                      {this.canCancel(booking) && (
                        <button className={styles.cancelButton} onClick={() => this.handleCancelBooking(booking.id)}>Cancel</button>
                      )}
                      {this.canRate(booking) && (
                        <button className={styles.arriveButton} onClick={() => this.openRating(booking)}>
                          {booking.customerRating ? `★ ${booking.customerRating}/5 · Edit rating` : 'Rate your visit'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {this.state.openChatBookingId && (
          <div className={styles.chatPanelOverlay}>
            <div className={styles.chatPanel}>
              <div className={styles.chatPanelHeader}>
                <span>Booking Chat</span>
                <button type="button" onClick={this.handleCloseBookingChat} aria-label="Close chat">×</button>
              </div>
              <BookingChat organizerName="Booking" branchName="—" bookingId={this.state.openChatBookingId} branchId={this.state.bookings.find(b => b.id === this.state.openChatBookingId)?.branchId} onClose={this.handleCloseBookingChat} />
            </div>
          </div>
        )}

        {showDetailModal && selectedBooking && (
          <div className={styles.modalOverlay} onClick={this.handleCloseModal}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>Booking Details</h2>
                <button className={styles.modalClose} onClick={this.handleCloseModal}>×</button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.modalSection}>
                  <h3>Booking Information</h3>
                  <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Status:</span><span>{this.getStatusBadge(selectedBooking.status)}</span></div>
                  <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Venue:</span><span className={styles.modalValue}>{selectedBooking.branch?.name || '—'}</span></div>
                  <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Table:</span><span className={styles.modalValue}>Table {selectedBooking.table?.tableNumber ?? ''}</span></div>
                  <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Date & Time:</span><span className={styles.modalValue}>{this.formatDateTime(selectedBooking.bookingDate, selectedBooking.startTime)}</span></div>
                  <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Guests:</span><span className={styles.modalValue}>{selectedBooking.numberOfCustomers}</span></div>
                  {selectedBooking.depositStatus && selectedBooking.depositStatus !== 'none' && (
                    <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Deposit:</span><span className={styles.modalValue}>{selectedBooking.depositAmount} ({selectedBooking.depositStatus})</span></div>
                  )}
                  {selectedBooking.cancellationReason && (
                    <div className={styles.modalDetailRow}><span className={styles.modalLabel}>Cancellation:</span><span className={styles.modalValue}>{selectedBooking.cancellationReason}</span></div>
                  )}
                </div>

                {this.canPreorder(selectedBooking) && (
                  <div className={styles.modalSection}>
                    <h3>Pre-order</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
                      Tell the venue what to have ready — food, drinks, or a song request.
                    </p>
                    <textarea
                      value={this.state.preorderDraft}
                      onChange={(e) => this.setState({ preorderDraft: e.target.value })}
                      rows={3}
                      placeholder="e.g. 2 cappuccinos on arrival; play 'Hotel California'"
                      style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid var(--border, #d1d5db)', fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    <button className={styles.arriveButton} disabled={this.state.savingPreorder} onClick={this.handleSavePreorder} style={{ marginTop: '0.6rem' }}>
                      {this.state.savingPreorder ? 'Saving…' : 'Save pre-order'}
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button className={styles.viewDetailsButton} onClick={() => { this.handleCloseModal(); this.handleOpenChat(selectedBooking.id); }}>Open Chat</button>
                <button className={styles.closeButton} onClick={this.handleCloseModal}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Request reschedule modal */}
        {this.state.reschedBooking && (
          <div className={styles.modalOverlay} onClick={this.closeReschedule}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>Request reschedule</h2>
                <button className={styles.modalClose} onClick={this.closeReschedule}>×</button>
              </div>
              <div className={styles.modalBody}>
                <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                  Propose a new time for {this.state.reschedBooking.branch?.name || 'your booking'}. The venue will approve or decline.
                </p>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>New date</label>
                <input type="date" value={this.state.reschedDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => this.setState({ reschedDate: e.target.value })} style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid var(--border, #d1d5db)', marginBottom: '0.75rem' }} />
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>New start time</label>
                <input type="time" value={this.state.reschedTime} onChange={(e) => this.setState({ reschedTime: e.target.value })} style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid var(--border, #d1d5db)', marginBottom: '0.75rem' }} />
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Reason (optional)</label>
                <input type="text" value={this.state.reschedReason} onChange={(e) => this.setState({ reschedReason: e.target.value })} placeholder="e.g. running late" style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid var(--border, #d1d5db)' }} />
              </div>
              <div className={styles.modalFooter}>
                <button className={styles.arriveButton} disabled={this.state.savingResched} onClick={this.submitReschedule}>
                  {this.state.savingResched ? 'Sending…' : 'Send request'}
                </button>
                <button className={styles.closeButton} onClick={this.closeReschedule}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Rate your visit modal */}
        {this.state.ratingBooking && (
          <div className={styles.modalOverlay} onClick={this.closeRating}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>Rate your visit</h2>
                <button className={styles.modalClose} onClick={this.closeRating}>×</button>
              </div>
              <div className={styles.modalBody}>
                <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                  {this.state.ratingBooking.branch?.name || 'How was your visit?'}
                </p>
                <div style={{ display: 'flex', gap: '0.35rem', fontSize: '2.25rem', cursor: 'pointer', justifyContent: 'center', margin: '0.5rem 0 1rem' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      role="button"
                      aria-label={`${star} star${star > 1 ? 's' : ''}`}
                      onClick={() => this.setState({ ratingValue: star })}
                      style={{ color: star <= this.state.ratingValue ? '#f59e0b' : 'var(--border, #d1d5db)', lineHeight: 1 }}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <textarea
                  value={this.state.ratingText}
                  onChange={(e) => this.setState({ ratingText: e.target.value })}
                  rows={3}
                  placeholder="Tell us about your experience (optional)"
                  style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid var(--border, #d1d5db)', fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>
              <div className={styles.modalFooter}>
                <button className={styles.arriveButton} disabled={this.state.savingRating} onClick={this.submitRating}>
                  {this.state.savingRating ? 'Submitting…' : 'Submit rating'}
                </button>
                <button className={styles.closeButton} onClick={this.closeRating}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </>
    );
  }
}

export default withNavigation(MyBookings);
